// Quests editor — kill objectives only for now (matches the M5.2 scope).
// Master/detail shell + dirty bookkeeping delegated to
// `createCatalogueEditor`. This file owns: blank/sanitize, the per-row
// form (identity, description, objectives, rewards, speech), and the
// objective/reward sub-editors with their kind-dependent shapes.

import type { QuestObjective, QuestReward, QuestTemplate } from "@vinyl/shared";
import {
  type CatalogueEditor,
  createCatalogueEditor,
} from "./catalogue-editor.js";
import { createCombobox } from "./combobox.js";
import { el } from "./dom.js";
import { checkboxField, textField } from "./form-fields.js";

export interface QuestsScreenCallbacks {
  getKnownMobIds: () => string[];
  getKnownItemIds: () => string[];
  onDirtyChange: (dirty: boolean) => void;
}

export interface QuestsScreen {
  root: HTMLElement;
  setQuests(quests: readonly QuestTemplate[]): void;
  getDraft(): QuestTemplate[];
  isDirty(): boolean;
  markPublished(): void;
}

function blank(): QuestTemplate {
  return {
    id: "new_quest",
    name: "New Quest",
    description: "Quest description",
    objectives: [{ kind: "kill", mobTemplateId: "cave_rat", count: 1 }],
    rewards: [{ kind: "xp", amount: 10 }],
  };
}

function sanitize(q: QuestTemplate): QuestTemplate {
  const speech = q.speech;
  return {
    id: q.id,
    name: q.name,
    description: q.description,
    objectives: q.objectives.map((o) => ({ ...o })),
    rewards: q.rewards.map((r) => ({ ...r })),
    ...(speech &&
    ((speech.onAccept?.trim() ?? "") || (speech.onTurnIn?.trim() ?? ""))
      ? {
          speech: {
            ...(speech.onAccept?.trim()
              ? { onAccept: speech.onAccept.trim() }
              : {}),
            ...(speech.onTurnIn?.trim()
              ? { onTurnIn: speech.onTurnIn.trim() }
              : {}),
          },
        }
      : {}),
    ...(q.repeatable ? { repeatable: true } : {}),
  };
}

export function createQuestsScreen(cb: QuestsScreenCallbacks): QuestsScreen {
  const editor: CatalogueEditor<QuestTemplate> = createCatalogueEditor<QuestTemplate>(
    {
      entityName: "quest",
      getName: (q) => q.name,
      blank,
      sanitize,
      showFilter: false,
      renderEditor: ({ draft, markDirty, rerender }) =>
        renderForm(draft, markDirty, rerender),
    },
    { onDirtyChange: cb.onDirtyChange },
  );

  function renderForm(
    q: QuestTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    return el(
      "div",
      {},
      el("h2", {}, q.name || "(unnamed)"),
      section("Identity", [
        textField("id", q.id, (v) => {
          q.id = v;
          markDirty();
        }),
        textField("Name", q.name, (v) => {
          q.name = v;
          markDirty();
        }),
        checkboxField("Repeatable", q.repeatable ?? false, (v) => {
          if (v) q.repeatable = true;
          else delete q.repeatable;
          markDirty();
        }),
      ]),
      el(
        "div",
        { class: "editor-section" },
        el("h3", {}, "Description"),
        textarea(q.description, (v) => {
          q.description = v;
          markDirty();
        }),
      ),
      renderObjectives(q, markDirty, rerender),
      renderRewards(q, markDirty, rerender),
      el(
        "div",
        { class: "editor-section" },
        el("h3", {}, "Speech (optional)"),
        el(
          "div",
          { class: "editor-grid" },
          textField("On accept", q.speech?.onAccept ?? "", (v) => {
            q.speech = { ...(q.speech ?? {}), onAccept: v };
            markDirty();
          }),
          textField("On turn-in", q.speech?.onTurnIn ?? "", (v) => {
            q.speech = { ...(q.speech ?? {}), onTurnIn: v };
            markDirty();
          }),
        ),
      ),
      el(
        "div",
        { class: "editor-actions" },
        el(
          "button",
          {
            class: "button danger",
            onclick: () => {
              if (!confirm(`Delete "${q.name}"?`)) return;
              const next = editor.getDraft().filter((d) => d.id !== q.id);
              editor.set(next);
              cb.onDirtyChange(true);
            },
          },
          "Delete from draft",
        ),
      ),
    );
  }

  function renderObjectives(
    q: QuestTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const knownMobs = cb.getKnownMobIds();
    const rows = el("div", { class: "affix-list" });
    for (let i = 0; i < q.objectives.length; i++) {
      const o = q.objectives[i]!;
      rows.append(renderObjectiveRow(q, i, o, knownMobs, markDirty, rerender));
    }
    return el(
      "div",
      { class: "editor-section" },
      el("h3", {}, `Objectives (${q.objectives.length}/5)`),
      rows,
      el(
        "div",
        { class: "affix-actions" },
        el(
          "button",
          {
            class: "button secondary",
            type: "button",
            onclick: () => {
              q.objectives = [
                ...q.objectives,
                { kind: "kill", mobTemplateId: knownMobs[0] ?? "cave_rat", count: 1 },
              ];
              markDirty();
              rerender();
            },
          },
          "+ Add objective",
        ),
      ),
    );
  }

  function renderObjectiveRow(
    q: QuestTemplate,
    idx: number,
    obj: QuestObjective,
    knownMobs: readonly string[],
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    if (obj.kind !== "kill") return el("div");
    const row = el("div", { class: "objective-row" });
    row.append(
      createCombobox({
        options: knownMobs,
        value: obj.mobTemplateId,
        onChange: (v) => {
          q.objectives = q.objectives.map((x, i) =>
            i === idx ? { ...x, mobTemplateId: v } : x,
          );
          markDirty();
        },
        placeholder: "Search mobs…",
      }),
    );
    row.append(
      numInput(obj.count, (v) => {
        q.objectives = q.objectives.map((x, i) =>
          i === idx ? { ...x, count: Math.max(1, Math.floor(v)) } : x,
        );
        markDirty();
      }),
    );
    row.append(
      el(
        "button",
        {
          class: "button danger",
          type: "button",
          title: "Remove",
          onclick: () => {
            q.objectives = q.objectives.filter((_, i) => i !== idx);
            markDirty();
            rerender();
          },
        },
        "×",
      ),
    );
    return row;
  }

  function renderRewards(
    q: QuestTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const rows = el("div", { class: "affix-list" });
    for (let i = 0; i < q.rewards.length; i++) {
      const r = q.rewards[i]!;
      rows.append(renderRewardRow(q, i, r, markDirty, rerender));
    }
    return el(
      "div",
      { class: "editor-section" },
      el("h3", {}, `Rewards (${q.rewards.length}/5)`),
      rows,
      el(
        "div",
        { class: "affix-actions" },
        el(
          "button",
          {
            class: "button secondary",
            type: "button",
            onclick: () => {
              q.rewards = [...q.rewards, { kind: "copper", amount: 50 }];
              markDirty();
              rerender();
            },
          },
          "+ Add reward",
        ),
      ),
    );
  }

  function renderRewardRow(
    q: QuestTemplate,
    idx: number,
    r: QuestReward,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const knownItems = cb.getKnownItemIds();
    const row = el("div", { class: "reward-row" });

    // Kind dropdown — switching kinds rebuilds the reward with sensible defaults.
    const kindSel = el(
      "select",
      {
        onchange: (e: Event) => {
          const kind = (e.target as HTMLSelectElement).value as QuestReward["kind"];
          let next: QuestReward;
          if (kind === "copper" || kind === "xp") {
            next = { kind, amount: r.kind === "item" ? 10 : r.amount };
          } else {
            next = {
              kind: "item",
              templateId: knownItems[0] ?? "rusty_dagger",
              qty: 1,
            };
          }
          q.rewards = q.rewards.map((x, i) => (i === idx ? next : x));
          markDirty();
          rerender();
        },
      },
    );
    for (const k of ["copper", "xp", "item"] as const) {
      const opt = el("option", { value: k }, k);
      if (k === r.kind) (opt as HTMLOptionElement).selected = true;
      kindSel.append(opt);
    }
    row.append(kindSel);

    if (r.kind === "item") {
      row.append(
        createCombobox({
          options: knownItems,
          value: r.templateId,
          onChange: (v) => {
            q.rewards = q.rewards.map((x, i) =>
              i === idx ? { ...x, templateId: v } : x,
            );
            markDirty();
          },
          placeholder: "Search items…",
        }),
      );
      row.append(
        numInput(r.qty, (v) => {
          q.rewards = q.rewards.map((x, i) =>
            i === idx ? { ...x, qty: Math.max(1, Math.min(99, Math.floor(v))) } : x,
          );
          markDirty();
        }),
      );
    } else {
      row.append(
        numInput(r.amount, (v) => {
          q.rewards = q.rewards.map((x, i) =>
            i === idx ? { ...x, amount: Math.max(0, Math.floor(v)) } : x,
          );
          markDirty();
        }),
      );
      row.append(el("span"));
    }

    row.append(
      el(
        "button",
        {
          class: "button danger",
          type: "button",
          title: "Remove",
          onclick: () => {
            q.rewards = q.rewards.filter((_, i) => i !== idx);
            markDirty();
            rerender();
          },
        },
        "×",
      ),
    );
    return row;
  }

  return {
    root: editor.root,
    setQuests(quests) {
      editor.set(
        quests.map((q) => ({
          ...q,
          objectives: q.objectives.map((o) => ({ ...o })),
          rewards: q.rewards.map((r) => ({ ...r })),
          ...(q.speech ? { speech: { ...q.speech } } : {}),
        })),
      );
    },
    getDraft: editor.getDraft,
    isDirty: editor.isDirty,
    markPublished: editor.markPublished,
  };
}

// Local helpers — kept in-file to avoid bloating form-fields.ts.

function section(title: string, children: HTMLElement[]): HTMLElement {
  return el(
    "div",
    { class: "editor-section" },
    el("h3", {}, title),
    el("div", { class: "editor-grid" }, ...children),
  );
}

function textarea(value: string, onChange: (v: string) => void): HTMLElement {
  return el(
    "textarea",
    {
      rows: 3,
      style: "width:100%;resize:vertical",
      oninput: (e: Event) => onChange((e.target as HTMLTextAreaElement).value),
    },
    value,
  );
}

function numInput(value: number, onChange: (v: number) => void): HTMLInputElement {
  return el("input", {
    type: "number",
    value: String(value),
    step: "1",
    oninput: (e: Event) => {
      const v = Number.parseInt((e.target as HTMLInputElement).value, 10);
      if (Number.isFinite(v)) onChange(v);
    },
  }) as HTMLInputElement;
}
